import {t} from "@lingui/core/macro";
import {useEffect} from "react";
// eslint-disable-next-line no-restricted-imports -- porting form old code.
import {useHistory} from "react-router";
import type {Editor} from "slate";

import {hasUnsavedChanges} from "../slate/helpers/insertion-helpers.ts";

/**
 * This hook adds listeners to detect (and ask for confirmation) when the
 * browser window is going to close/reload/change URL and the given editor
 * has unsaved changes.
 *
 * We need to do two separate-but-similar things here: add a `beforeunload`
 * listener to the window that uses the browser-native dialog for checking
 * for unsaved changes (which is not customizable), and use `history.block` to
 * listen for (and potentially block) React Router's client-side navigation.
 *
 * WARNING: this hook depends on the RRv5 API.  It is not compatible with
 * RRv6.
 */
export default function useRRv5CheckForUnsavedChanges(
    editor: Editor | null,
    viewOnly: boolean,
) {
    const history = useHistory();

    useEffect(() => {
        if (!editor || viewOnly) {
            return;
        }

        const handler = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges(editor)) {
                event.returnValue = "1";
                event.preventDefault();
                return true;
            }
        };

        const unblock = history.block(() => {
            if (hasUnsavedChanges(editor)) {
                return t`Are you sure you want to leave? Your document won't be saved unless you click 'Print' or 'Export'.`;
            }
        });

        window.addEventListener("beforeunload", handler);

        return () => {
            window.removeEventListener("beforeunload", handler);
            unblock();
        };
    }, [editor, history, viewOnly]);
}
